import CodeMirror from "codemirror";
import has from "lodash/has";
import { exitCode } from "prosemirror-commands";
import { redo, undo } from "prosemirror-history";
import { Selection, TextSelection } from "prosemirror-state";
import { getModeByLanguageName, languageImports } from "./constants";
export class CodeBlockView {
    constructor(node, view, getPos) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        /**
         * Track whether changes are have been made but not yet propagated
         */
        this.incomingChanges = false;
        /**
         * This flag is used to avoid an update loop between the outer and inner editor
         */
        this.updating = false;
        const attrs = this.node.attrs;
        // Create a CodeMirror instance
        this.cm = CodeMirror(null, {
            value: this.node.textContent,
            lineWrapping: attrs.lineWrapping,
            lineNumbers: true,
            viewportMargin: Infinity,
            extraKeys: this.codeMirrorKeymap(),
            mode: getModeByLanguageName(attrs.language) || null,
        });
        // The editor's outer node is our DOM representation
        this.dom = this.cm.getWrapperElement();
        // Lazy load the language mode
        this.setMode(this.cm.getOption("mode"));
        // CodeMirror needs to be in the DOM to properly initialize, so
        // schedule it to update itself
        setTimeout(() => this.cm.refresh(), 200);
        // Track whether changes are have been made but not yet propagated
        this.cm.on("beforeChange", () => (this.incomingChanges = true));
        // Propagate updates from the code editor to ProseMirror
        this.cm.on("cursorActivity", () => {
            if (!this.updating && !this.incomingChanges) {
                this.forwardSelection();
            }
        });
        this.cm.on("changes", () => {
            if (!this.updating) {
                this.valueChanged(view.state.schema);
                this.forwardSelection();
            }
            this.incomingChanges = false;
        });
        this.cm.on("focus", () => this.forwardSelection());
    }
    update(node) {
        if (node.type !== this.node.type) {
            return false;
        }
        const newAttrs = node.attrs;
        const currentAttrs = this.node.attrs;
        if (newAttrs.language !== currentAttrs.language) {
            this.setMode(getModeByLanguageName(newAttrs.language));
        }
        if (newAttrs.lineWrapping !== currentAttrs.lineWrapping) {
            this.cm.setOption("lineWrapping", newAttrs.lineWrapping);
        }
        this.node = node;
        const change = computeChange(this.cm.getValue(), node.textContent);
        if (change) {
            this.updating = true;
            this.cm.replaceRange(change.text, this.cm.posFromIndex(change.from), this.cm.posFromIndex(change.to));
            this.updating = false;
        }
        return true;
    }
    selectNode() {
        this.cm.focus();
    }
    stopEvent() {
        return true;
    }
    setSelection(anchor, head) {
        this.cm.focus();
        this.updating = true;
        this.cm.setSelection(this.cm.posFromIndex(anchor), this.cm.posFromIndex(head));
        this.updating = false;
    }
    setMode(mode) {
        if (has(CodeMirror.modes, mode)) {
            this.cm.setOption("mode", mode);
        }
        const modeImport = languageImports[mode];
        if (modeImport) {
            modeImport()
                .then(() => {
                this.cm.setOption("mode", mode);
            })
                .catch((error) => {
                if (process.env.NODE_ENV === "development") {
                    // eslint-disable-next-line no-console
                    console.error(`Error trying to load CodeMirror mode: "${mode}"`, error);
                }
            });
        }
    }
    forwardSelection() {
        if (!this.cm.hasFocus()) {
            return;
        }
        const state = this.view.state;
        const selection = this.asProseMirrorSelection(state.doc);
        if (!selection.eq(state.selection)) {
            this.view.dispatch(state.tr.setSelection(selection));
        }
    }
    asProseMirrorSelection(doc) {
        const offset = this.getPos() + 1;
        const anchor = this.cm.indexFromPos(this.cm.getCursor("anchor")) + offset;
        const head = this.cm.indexFromPos(this.cm.getCursor("head")) + offset;
        return TextSelection.create(doc, anchor, head);
    }
    valueChanged(schema) {
        const change = computeChange(this.node.textContent, this.cm.getValue());
        if (change) {
            const start = this.getPos() + 1;
            const tr = this.view.state.tr.replaceWith(start + change.from, start + change.to, change.text ? schema.text(change.text) : null);
            this.view.dispatch(tr);
        }
    }
    codeMirrorKeymap() {
        const view = this.view;
        const mod = navigator.platform.includes("Mac") ? "Cmd" : "Ctrl";
        return CodeMirror.normalizeKeyMap({
            Up: () => this.maybeEscape("line", -1),
            Left: () => this.maybeEscape("char", -1),
            Down: () => this.maybeEscape("line", 1),
            Right: () => this.maybeEscape("char", 1),
            // eslint-disable-next-line @typescript-eslint/unbound-method
            [`${mod}-Z`]: () => undo(view.state, view.dispatch),
            // eslint-disable-next-line @typescript-eslint/unbound-method
            [`Shift-${mod}-Z`]: () => redo(view.state, view.dispatch),
            // eslint-disable-next-line @typescript-eslint/unbound-method
            [`${mod}-Y`]: () => redo(view.state, view.dispatch),
            "Shift-Enter": () => {
                // eslint-disable-next-line @typescript-eslint/unbound-method
                if (exitCode(view.state, view.dispatch)) {
                    view.focus();
                }
            },
        });
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    maybeEscape(unit, dir) {
        const pos = this.cm.getCursor();
        if (this.cm.somethingSelected() ||
            pos.line !== (dir < 0 ? this.cm.firstLine() : this.cm.lastLine()) ||
            (unit === "char" &&
                pos.ch !== (dir < 0 ? 0 : this.cm.getLine(pos.line).length))) {
            return CodeMirror.Pass;
        }
        this.view.focus();
        const targetPos = this.getPos() + (dir < 0 ? 0 : this.node.nodeSize);
        const selection = Selection.near(this.view.state.doc.resolve(targetPos), dir);
        this.view.dispatch(this.view.state.tr.setSelection(selection).scrollIntoView());
        this.view.focus();
    }
}
function computeChange(oldVal, newVal) {
    if (oldVal === newVal) {
        return null;
    }
    let start = 0;
    let oldEnd = oldVal.length;
    let newEnd = newVal.length;
    while (start < oldEnd &&
        oldVal.charCodeAt(start) === newVal.charCodeAt(start)) {
        ++start;
    }
    while (oldEnd > start &&
        newEnd > start &&
        oldVal.charCodeAt(oldEnd - 1) === newVal.charCodeAt(newEnd - 1)) {
        oldEnd--;
        newEnd--;
    }
    return { from: start, to: oldEnd, text: newVal.slice(start, newEnd) };
}
